import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { Link } from "wouter";
import {
  Stethoscope,
  LogOut,
  Loader2,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Globe,
  Plus,
  Trash2,
  Shield,
  Settings as SettingsIcon,
  History,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SEO } from "@/components/SEO";

type Profile = {
  _id: Id<"profiles">;
  userId: Id<"users">;
  name: string;
  prompt: string;
  createdAt: number;
  updatedAt: number;
};

export default function Profiles() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [userSynced, setUserSynced] = useState(false);
  const [selectedId, setSelectedId] = useState<Id<"profiles"> | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [savedName, setSavedName] = useState("");
  const [savedPrompt, setSavedPrompt] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<Id<"profiles"> | null>(null);

  const getOrCreateUser = useMutation(api.users.getOrCreateUser);
  const createProfile = useMutation(api.profiles.create);
  const updateProfile = useMutation(api.profiles.update);
  const removeProfile = useMutation(api.profiles.remove);

  useEffect(() => {
    if (user && !userSynced) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName
          ? `${user.firstName} ${user.lastName || ""}`.trim()
          : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      })
        .then(() => setUserSynced(true))
        .catch((err) => console.error("Failed to sync user with Convex:", err));
    }
  }, [user, userSynced, getOrCreateUser]);

  const convexUser = useQuery(
    api.users.getCurrentUser,
    user && userSynced ? { googleId: user.id } : "skip",
  );
  const isAdmin = useIsAdmin();
  const profiles = useQuery(
    api.profiles.list,
    convexUser ? { userId: convexUser._id } : "skip",
  ) as Profile[] | undefined;

  // Keep a valid selection when profiles load/change.
  useEffect(() => {
    if (isNew) return;
    if (!profiles) return;
    if (selectedId && profiles.some((p) => p._id === selectedId)) return;
    if (profiles.length > 0) {
      const first = profiles[0];
      setSelectedId(first._id);
      setName(first.name);
      setPrompt(first.prompt);
      setSavedName(first.name);
      setSavedPrompt(first.prompt);
    } else {
      setSelectedId(null);
      setName("");
      setPrompt("");
      setSavedName("");
      setSavedPrompt("");
    }
  }, [profiles, selectedId, isNew]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    window.location.href = "/api/login";
    return null;
  }

  if (!userSynced || !convexUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isDirty = name !== savedName || prompt !== savedPrompt;

  const confirmDiscardIfDirty = () => {
    if (!isDirty && !isNew) return true;
    if (!isDirty && isNew && !name.trim() && !prompt.trim()) return true;
    if (!isDirty) return true;
    return window.confirm(
      "You have unsaved changes. Discard them and continue?",
    );
  };

  const handleSelect = (p: Profile) => {
    if (p._id === selectedId && !isNew) return;
    if (!confirmDiscardIfDirty()) return;
    setIsNew(false);
    setSelectedId(p._id);
    setName(p.name);
    setPrompt(p.prompt);
    setSavedName(p.name);
    setSavedPrompt(p.prompt);
  };

  const handleNew = () => {
    if (!confirmDiscardIfDirty()) return;
    setIsNew(true);
    setSelectedId(null);
    setName("");
    setPrompt("");
    setSavedName("");
    setSavedPrompt("");
  };

  const handleSave = async () => {
    if (isSaving) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast({
        title: "Name required",
        description: "Give this profile a name before saving.",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    try {
      if (isNew || !selectedId) {
        const newId = await createProfile({
          userId: convexUser._id,
          name: trimmedName,
          prompt,
        });
        setIsNew(false);
        setSelectedId(newId);
        toast({ title: "Profile created" });
      } else {
        await updateProfile({
          id: selectedId,
          userId: convexUser._id,
          name: trimmedName,
          prompt,
        });
        toast({ title: "Saved" });
      }
      setSavedName(trimmedName);
      setSavedPrompt(prompt);
      setName(trimmedName);
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to save",
        description:
          err instanceof Error ? err.message : "Error saving profile.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await removeProfile({ id: deletingId, userId: convexUser._id });
      if (selectedId === deletingId) {
        setSelectedId(null);
        setIsNew(false);
        setName("");
        setPrompt("");
        setSavedName("");
        setSavedPrompt("");
      }
      toast({ title: "Profile deleted" });
    } catch (err) {
      toast({
        title: "Failed to delete",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const deletingProfile = (profiles || []).find((p) => p._id === deletingId);
  const showEditor = isNew || selectedId !== null;

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Profiles — LRScribe" noindex />
      <header className="relative h-[26px] border-b border-border bg-card flex items-center justify-between px-2 shrink-0 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href="/dashboard"
            className="flex items-center gap-1 shrink-0"
            aria-label="LRScribe home"
          >
            <div className="p-0.5 bg-primary/10 rounded-sm">
              <Stethoscope className="w-3 h-3 text-primary" />
            </div>
            <span className="font-heading font-bold text-xs text-foreground hidden sm:inline leading-none">
              LRScribe
            </span>
          </Link>
        </div>

        <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Link
            href="/dashboard"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-dashboard"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            href="/templates"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-templates"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Templates</span>
          </Link>
          <Link
            href="/rules"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-rules"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Rules</span>
          </Link>
          <Link
            href="/profiles"
            className="text-xs font-medium text-primary transition-colors flex items-center gap-1 leading-none"
            data-testid="link-profiles"
          >
            <UserRound className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Profiles</span>
          </Link>
          <Link
            href="/chat"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </Link>
        </nav>

        <div className="flex items-center gap-0.5 shrink-0">
          <ThemeToggle className="h-5 w-5 [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background cursor-pointer"
                data-testid="button-user-menu"
              >
                <Avatar className="h-5 w-5 pointer-events-none">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div
                  className="text-sm font-medium text-foreground truncate"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4 mr-2" />
                  Change log
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings" data-testid="link-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Left pane: profiles list */}
        <aside className="w-72 border-r border-border bg-card/30 flex flex-col shrink-0">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <UserRound className="w-4 h-4" />
                Profiles
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {(profiles || []).length} total
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleNew}
              data-testid="button-new-profile"
            >
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {profiles === undefined ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : profiles.length === 0 && !isNew ? (
                <p className="text-xs text-muted-foreground text-center py-8 px-4">
                  No profiles yet. Create one to store a reusable prompt.
                </p>
              ) : (
                profiles.map((p) => (
                  <div
                    key={p._id}
                    className={`group flex items-center justify-between gap-2 rounded-md px-3 py-2 cursor-pointer transition-colors ${
                      selectedId === p._id && !isNew
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/50 text-foreground"
                    }`}
                    onClick={() => handleSelect(p)}
                    data-testid={`profile-item-${p._id}`}
                  >
                    <span className="text-sm truncate">{p.name}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingId(p._id);
                      }}
                      aria-label={`Delete profile ${p.name}`}
                      data-testid={`button-delete-profile-${p._id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
              {isNew && (
                <div
                  className="flex items-center rounded-md px-3 py-2 bg-primary/10 text-primary"
                  data-testid="profile-item-new"
                >
                  <span className="text-sm truncate italic">
                    {name.trim() || "New profile"}
                  </span>
                </div>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Right pane: editor */}
        <main className="flex-1 flex flex-col min-w-0">
          {showEditor ? (
            <div className="flex-1 flex flex-col p-6 gap-4 min-h-0">
              <div className="flex items-center justify-between gap-4">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Profile name"
                  className="max-w-sm"
                  data-testid="input-profile-name"
                />
                <div className="flex items-center gap-2 shrink-0">
                  {!isNew && selectedId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingId(selectedId)}
                      className="text-destructive hover:text-destructive"
                      data-testid="button-delete-profile"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || (!isDirty && !isNew)}
                    data-testid="button-save-profile"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : null}
                    Save
                  </Button>
                </div>
              </div>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Write the prompt for this profile..."
                className="flex-1 resize-none font-mono text-sm min-h-0"
                data-testid="textarea-profile-prompt"
              />
              {isDirty && (
                <p className="text-xs text-muted-foreground">
                  Unsaved changes
                </p>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-sm px-6">
                <UserRound className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  Select a profile on the left or create a new one to get
                  started.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      <AlertDialog
        open={deletingId !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete profile?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingProfile
                ? `"${deletingProfile.name}" and its prompt will be permanently deleted. This cannot be undone.`
                : "This profile will be permanently deleted. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-profile">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-profile"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
